import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Save, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CreateEditPost = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    categories: '',
    image_url: '',
    youtube_url: ''
  });

  const isEditing = Boolean(postId);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);
        
        // Verificar se é admin antes de permitir criar/editar posts
        if (userRes.data.email !== 'fablucvi@gmail.com') {
          alert('Acesso negado. Apenas o administrador pode criar ou editar posts.');
          navigate('/');
          return;
        }

        if (isEditing) {
          setLoading(true);
          const postRes = await axios.get(`${API}/posts/${postId}`);
          const post = postRes.data;

          setFormData({
            title: post.title,
            content: post.content,
            categories: post.categories.join(', '),
            image_url: post.image_url || '',
            youtube_url: post.youtube_url || ''
          });
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 403) {
          alert('Acesso negado. Apenas o administrador pode criar ou editar posts.');
        }
        navigate('/dashboard');
      }
    };

    fetchData();
  }, [postId, isEditing, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Título e conteúdo são obrigatórios');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        title: formData.title,
        content: formData.content,
        categories: formData.categories
          .split(',')
          .map((c) => c.trim())
          .filter((c) => c),
        image_url: formData.image_url || null,
        youtube_url: formData.youtube_url || null
      };

      if (isEditing) {
        await axios.put(`${API}/posts/${postId}`, data, { withCredentials: true });
        alert('Post atualizado com sucesso!');
      } else {
        await axios.post(`${API}/posts`, data, { withCredentials: true });
        alert('Post criado com sucesso!');
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Erro ao salvar post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4 tracking-tight leading-tight">
            {isEditing ? 'Editar Post' : 'Criar Novo Post'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="post-form">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Título *
            </label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Título do post"
              className="rounded-md"
              required
              data-testid="post-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Conteúdo *
            </label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Escreva o conteúdo do seu post..."
              className="rounded-md"
              rows={15}
              required
              data-testid="post-content-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Categorias (separadas por vírgula)
            </label>
            <Input
              type="text"
              value={formData.categories}
              onChange={(e) => setFormData({ ...formData, categories: e.target.value })}
              placeholder="Tecnologia, Programação, Web"
              className="rounded-md"
              data-testid="post-categories-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              URL da Imagem (opcional)
            </label>
            <Input
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://exemplo.com/imagem.jpg"
              className="rounded-md"
              data-testid="post-image-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              URL do YouTube (opcional)
            </label>
            <Input
              type="url"
              value={formData.youtube_url}
              onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
              className="rounded-md"
              data-testid="post-youtube-input"
            />
            <p className="text-xs text-stone-500 mt-1">
              Cole o link completo do vídeo do YouTube (ex: https://www.youtube.com/watch?v=dQw4w9WgXcQ)
            </p>
          </div>

          {formData.image_url && (
            <div className="rounded-lg overflow-hidden border border-stone-200">
              <img
                src={formData.image_url}
                alt="Preview"
                className="w-full h-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-md"
              data-testid="submit-post-button"
            >
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {isEditing ? 'Atualizar Post' : 'Criar Post'}
            </Button>
            <Button
              type="button"
              onClick={handleCancel}
              variant="ghost"
              className="rounded-md"
              data-testid="cancel-post-button"
            >
              <X className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Cancelar
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateEditPost;